/**
 * looksLikeMarkdown — heuristic detector for "is this text actually authored
 * Markdown" vs. a plain conversational string.
 *
 * Used by the transcript / chat renderers to decide whether a *user* message
 * should be rendered through `ChatMarkdown` (so headings, bold, lists, code
 * fences, tables, etc. render structurally) instead of being dumped into a
 * plain `<p>` where the raw `##` / `**` / `1.` syntax shows literally as a
 * wall of text.
 *
 * The detector is intentionally conservative: a short plain message like
 * "hello there" or a multi-line note without any Markdown tokens returns
 * `false` so it keeps the existing pre-wrap plain-text rendering (Markdown
 * would otherwise collapse single newlines into spaces). It only returns
 * `true` when at least one structural Markdown token is present.
 */

/** Ordered list of structural Markdown signals. Any match → treat as Markdown. */
const MARKDOWN_SIGNALS: RegExp[] = [
  /^#{1,6}\s+\S/m, // ATX heading:           "## Heading"
  /\*\*[^\s*][^*]*\*\*/, // bold:             "**label**"
  /__[^\s_][^_]*__/, // bold (underscore):    "__label__"
  /^\s*[-*+]\s+\S/m, // bullet list item:     "- item"
  /^\s*\d+\.\s+\S/m, // ordered list item:    "1. item"
  /^\s*>\s+\S/m, // blockquote:               "> quote"
  /```/, // fenced code block:                "```"
  /`[^`\n]+`/, // inline code:                "`code`"
  /^\s*\|.+\|\s*$/m, // table row:            "| a | b |"
  /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/m, // thematic break / hr
  /\[[^\]]+\]\([^)\s]+\)/, // link:           "[text](url)"
  /!\[[^\]]*\]\([^)\s]+\)/, // image:         "![alt](url)"
]

/**
 * Returns true when `text` contains at least one structural Markdown token.
 *
 * @param text - raw message text (may be empty / undefined-safe via guard).
 */
export function looksLikeMarkdown(text: string | null | undefined): boolean {
  if (!text) return false
  return MARKDOWN_SIGNALS.some((re) => re.test(text))
}

export default looksLikeMarkdown
